import { FC, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'components/Button/Button'
import { MyDropdown } from 'components/ReactHookForm/MyDropdown'
import { ClaimsSortForm, ClaimSortType } from 'models/claims'
import { logAriaMessage } from 'redux/reducers/aria-log'
import { getSortType, sortClaims } from 'redux/reducers/claims'
import classNames from 'classnames'

export interface ClaimsSortProps {
  className?: string
}

export const ClaimsSort: FC<ClaimsSortProps> = ({ className }) => {
  const { t } = useTranslation('claimsSearch', { keyPrefix: 'sort' })
  const dispatch = useDispatch()
  const sortType = useSelector(getSortType)

  const formMethods = useForm<ClaimsSortForm>({
    defaultValues: { sortType }
  })
  const handleSubmit: SubmitHandler<ClaimsSortForm> = useCallback(
    (formValues: ClaimsSortForm) => {
      dispatch(sortClaims({ sortType: formValues.sortType }))
      dispatch(logAriaMessage(t('sortComplete')))
    },
    [dispatch, t]
  )

  const dropdownOptions = [
    { label: t('newestServiceDate'), value: ClaimSortType.SERVICE_DATE_DESC },
    { label: t('oldestServiceDate'), value: ClaimSortType.SERVICE_DATE_ASC },
    { label: t('claimTypeAZ'), value: ClaimSortType.CLAIM_TYPE_ASC },
    { label: t('claimTypeZA'), value: ClaimSortType.CLAIM_TYPE_DESC },
    {
      label: t('highestMedicareApprovedAmount'),
      value: ClaimSortType.MEDICARE_APPROVED_AMOUNT_DESC
    },
    {
      label: t('lowestMedicareApprovedAmount'),
      value: ClaimSortType.MEDICARE_APPROVED_AMOUNT_ASC
    }
  ]

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={(e) => {
          formMethods.handleSubmit(handleSubmit)(e)
        }}
        className={classNames([
          'ds-u-display--flex',
          'ds-u-align-items--end',
          className
        ])}
      >
        <MyDropdown
          name="sortType"
          className=""
          label={t('sortByFieldLabel')}
          labelClassName="ds-c-label ds-u-margin-bottom--1"
          id="claims-sort-type"
          fieldClassName="ds-u-margin--0"
          options={dropdownOptions}
        />
        <Button
          className={classNames([
            'ds-c-button',
            'ds-c-button--solid',
            'ds-c-button--alternate',
            'ds-u-padding-x--4',
            'ds-u-padding-y--2'
          ])}
          type="submit"
          siteActivityProps={{
            core: {
              component: 'Claims',
              elementId: 'claims_sort_button',
              coreClickData: {
                // Pass custom link text to include the sort key without needing a separate event key
                linkText: `${t('sortButtonLabel')}: ${formMethods.getValues().sortType}`
              }
            }
          }}
        >
          {t('sortButtonLabel')}
        </Button>
      </form>
    </FormProvider>
  )
}
